import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import Badge from "@react/shared/ui/Badge"
import { fmt, dateFmt } from "../helpers/format"
import { fetchPurchaseOrderShow, sendPurchaseOrderEmail } from "../api/purchase_orders"

const STATUS_TONE = {
  "In preparation":           "slate",
  "Price request":            "blue",
  "Confirmed":                "violet",
  "Goods partially received": "amber",
  "Goods fully received":     "emerald",
}

const ts = (key, opts) => I18n.t(`projects.engagements.show.${key}`, opts)
const tf = (key) => I18n.t(`projects.engagements.form.${key}`)
const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
    <div className="text-[13px] text-slate-800 mt-0.5">{children || "—"}</div>
  </div>
)

const PurchaseOrderShowModal = ({ id, isOpen, onClose }) => {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(false)
  const [emailMode, setEmailMode] = useState(false)
  const [email, setEmail]     = useState({ recipient: "", subject: "", message: "" })
  const [sending, setSending] = useState(false)
  const [sent, setSent]       = useState(false)
  const [emailError, setEmailError] = useState(null)

  useEffect(() => {
    if (!isOpen || !id) return
    setLoading(true); setData(null); setEmailMode(false); setSent(false); setEmailError(null)
    fetchPurchaseOrderShow(id)
      .then((response) => {
        setData(response)
        setEmail({ recipient: response?.email?.recipient || "", subject: response?.email?.subject || "", message: "" })
      })
      .finally(() => setLoading(false))
  }, [isOpen, id])

  const handleSend = () => {
    setSending(true); setEmailError(null)
    sendPurchaseOrderEmail(id, email)
      .then((response) => {
        if (response?.ok) { setSent(true); setEmailMode(false) }
        else setEmailError((response?.errors || []).join(", ") || ts("email_error"))
      })
      .catch(() => setEmailError(ts("email_error")))
      .finally(() => setSending(false))
  }

  const footer = data && !loading && (
    emailMode ? (
      <>
        <div />
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="md" onClick={() => setEmailMode(false)} disabled={sending}>{ts("cancel")}</Button>
          <Button variant="primary" size="md" onClick={handleSend} disabled={sending || !email.recipient}>
            {sending && <FontAwesomeIcon icon="circle-notch" spin className="mr-1.5" />}{ts("send")}
          </Button>
        </div>
      </>
    ) : (
      <>
        <a href={data.show_path} className="text-[13px] text-slate-500 hover:text-slate-800 font-medium">
          <FontAwesomeIcon icon="arrow-up-right-from-square" className="mr-1.5" />{ts("go_to_card")}
        </a>
        <div className="flex items-center gap-2">
          <Button
            variant = "secondary"
            size    = "md"
            onClick = {() => {
              onClose()
              window.dispatchEvent(new CustomEvent("project:create-purchase-invoice", { detail: { purchaseOrderId: data.id } }))
            }}
          >
            <FontAwesomeIcon icon="file-invoice" className="mr-1.5" />{ts("create_invoice")}
          </Button>
          {data.can_download && (
            <a href={data.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13px] font-medium text-slate-700 border border-slate-300 hover:bg-slate-50">
              <FontAwesomeIcon icon="print" /> PDF
            </a>
          )}
          {data.can_send_email && (
            <Button variant="primary" size="md" onClick={() => { setEmailMode(true); setSent(false) }}>
              <FontAwesomeIcon icon="envelope" className="mr-1.5" />{ts("email")}
            </Button>
          )}
        </div>
      </>
    )
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} eyebrow={ts("eyebrow")} title={data?.display_title || ""} size="lg" footer={footer}>
      {loading || !data ? (
        <div className="py-16 text-center text-slate-400"><FontAwesomeIcon icon="circle-notch" spin className="text-2xl" /></div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Badge tone={STATUS_TONE[data.status] || "neutral"} variant="soft" size="sm">{data.status_label}</Badge>
            {data.invoice_status_label && <Badge tone="slate" variant="soft" size="sm">{data.invoice_status_label}</Badge>}
            {data.sent && <Badge tone="blue" variant="soft" size="sm"><FontAwesomeIcon icon="envelope" className="mr-1" />{ts("sent")}</Badge>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label={ts("supplier")}>{data.supplier?.name}</Field>
            <Field label={ts("manager")}>{data.manager}</Field>
            {data.category && <Field label={ts("category")}>{data.category}</Field>}
            {data.project && (
              <Field label={ts("project")}>
                <a href={data.project.path} className="text-brand-600 hover:underline">{data.project.name}</a>
              </Field>
            )}
            {data.project_task && <Field label={ts("lot")}>{data.project_task.title}</Field>}
            <Field label={ts("creation_date")}>{data.creation_date ? dateFmt(data.creation_date) : "—"}</Field>
            <Field label={ts("expected_delivery_date")}>{data.expected_delivery_date ? dateFmt(data.expected_delivery_date) : "—"}</Field>
            <Field label={ts("destination")}>{data.destination}</Field>
            <Field label={ts("delivery_address")}>{data.delivery_address}</Field>
          </div>

          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-[minmax(0,1fr)_70px_70px_110px_70px_110px] gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">
              <div>{tf("designation")}</div>
              <div className="text-right">{tf("quantity")}</div>
              <div className="text-center">{tf("unit")}</div>
              <div className="text-right">{tf("unit_price")}</div>
              <div className="text-center">{tf("vat")}</div>
              <div className="text-right">{tf("line_total")}</div>
            </div>
            {data.lines.length === 0 ? (
              <div className="px-4 py-6 text-center text-[13px] text-slate-400">{ts("no_lines")}</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.lines.map((line, index) => (
                  <div key={index} className="grid grid-cols-[minmax(0,1fr)_70px_70px_110px_70px_110px] gap-3 px-4 py-2 items-center text-[12.5px]">
                    <div className="min-w-0">
                      <div className="text-slate-800 truncate" title={stripHtml(line.description)}>{stripHtml(line.description) || "—"}</div>
                      {line.free_line && (
                        <div className="text-[11px] text-amber-700">{ts("free_line")}</div>
                      )}
                      {line.substitution && (
                        <div className="text-[11px] text-indigo-700 truncate">{ts("substitution", { label: line.substitution.replaces, price: line.substitution.quoted_unit_price?.formatted })}</div>
                      )}
                    </div>
                    <div className="text-right tabular-nums text-slate-600">{line.quantity ?? "—"}</div>
                    <div className="text-center text-slate-500">{line.unit || "—"}</div>
                    <div className="text-right tabular-nums text-slate-600">{fmt(line.unit_price)}</div>
                    <div className="text-center text-slate-500">{line.vat != null ? `${Math.round(line.vat)}%` : "—"}</div>
                    <div className="text-right tabular-nums text-slate-900 font-medium">{fmt(line.subtotal)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <div className="w-full md:w-72 rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-1.5">
              <div className="flex items-center justify-between text-[13px]"><span className="text-slate-500">{tf("subtotal_ht")}</span><span className="tabular-nums font-medium text-slate-900">{fmt(data.subtotal)}</span></div>
              <div className="flex items-center justify-between text-[12.5px]"><span className="text-slate-400">{tf("vat")}</span><span className="tabular-nums text-slate-600">{fmt(data.vat)}</span></div>
              <div className="flex items-center justify-between text-[15px] pt-1.5 border-t border-slate-200"><span className="font-semibold text-slate-700">{tf("total_ttc")}</span><span className="tabular-nums font-semibold text-slate-900">{fmt(data.total)}</span></div>
            </div>
          </div>

          {data.property_fields?.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium mb-2">{ts("custom_fields")}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {data.property_fields.map((field, index) => (
                  field.is_title ? (
                    <div key={index} className="col-span-2 md:col-span-3 text-[13px] font-semibold text-slate-800">{field.title}</div>
                  ) : (
                    <Field key={index} label={field.title}>
                      {field.link
                        ? <a href={field.link} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">{field.value}</a>
                        : field.value}
                    </Field>
                  )
                ))}
              </div>
            </div>
          )}

          {data.observation && stripHtml(data.observation) && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium mb-1">{ts("observation")}</div>
              <div className="text-[13px] text-slate-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: data.observation }} />
            </div>
          )}

          {sent && !emailMode && (
            <div className="px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
              <FontAwesomeIcon icon="check" className="mr-1.5" />{ts("email_sent")}
            </div>
          )}

          {emailMode && (
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h3 className="text-[13px] font-semibold text-slate-800"><FontAwesomeIcon icon="envelope" className="mr-1.5 text-slate-400" />{ts("email")}</h3>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">{ts("recipient")}<span className="text-rose-500"> *</span></label>
                <input className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={email.recipient} onChange={(event) => setEmail((previous) => ({ ...previous, recipient: event.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">{ts("subject")}</label>
                <input className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={email.subject} onChange={(event) => setEmail((previous) => ({ ...previous, subject: event.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">{ts("message")}</label>
                <textarea className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm" rows={3} value={email.message} onChange={(event) => setEmail((previous) => ({ ...previous, message: event.target.value }))} />
              </div>
              {emailError && <div className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">{emailError}</div>}
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

export default PurchaseOrderShowModal
